import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Img from 'gatsby-image';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 140px 0 0;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 0 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 80px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const ImageBlock = styled.div`
  position: relative;

  .img-main {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 540px;
    object-fit: cover;
    display: block;

    @media (max-width: 900px) {
      height: 350px;
    }
  }
`;

const Content = styled.div`
  padding-top: 20px;

  @media (max-width: 900px) {
    padding-top: 0;
  }

  .about-tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;

    &::before {
      content: '';
      width: 32px;
      height: 1px;
      background: #f08020;
    }
  }

  .about-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;
    margin-bottom: 28px;

    strong {
      font-weight: 600;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }

  .about-divider {
    width: 48px;
    height: 2px;
    background: #f08020;
    margin-bottom: 28px;
  }

  .about-desc {
    font-size: 15px;
    color: #5b6b7c;
    line-height: 1.9;
    white-space: pre-line;
    margin-bottom: 36px;
  }
`;

const StatsBar = styled.div`
  background: #1378e5;
  margin-top: 140px;
  padding: 72px 24px;

  @media (max-width: 768px) {
    margin-top: 80px;
    padding: 56px 24px;
  }
`;

const StatsGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 40px;
  text-align: center;
`;

const StatCard = styled.div`
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 50px;
    background: rgba(255, 255, 255, 0.18);

    @media (max-width: 768px) {
      display: none;
    }
  }

  .stat-number {
    font-size: 3rem;
    font-weight: 700;
    color: #f08020;
    line-height: 1;
    margin-bottom: 10px;

    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }

  .stat-label {
    font-size: 12px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

function AnimatedNumber({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const start = performance.now();

          const animate = now => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="stat-number">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function AboutUs({ tag, title, description, image, stats }) {
  return (
    <Section id="about-us">
      <Container>
        <Grid>
          <ImageBlock>
            {image?.asset && (
              <>
                {image.asset.fluid ? (
                  <Img
                    fluid={image.asset.fluid}
                    className="img-main"
                    style={{ height: '540px', zIndex: 1, position: 'relative' }}
                    imgStyle={{ objectFit: 'cover' }}
                    alt={tag || 'About us'}
                  />
                ) : image.asset.url ? (
                  <img
                    className="img-main"
                    src={image.asset.url}
                    alt={tag || 'About us'}
                  />
                ) : null}
              </>
            )}
          </ImageBlock>

          <Content>
            {tag && <div className="about-tag">{tag}</div>}
            <Branded as="h2" className="about-title" text={title} />
            <div className="about-divider" />
            <Branded as="p" className="about-desc" text={description} />
          </Content>
        </Grid>
      </Container>

      {stats && stats.length > 0 && (
        <StatsBar>
          <StatsGrid>
            {stats.map(({ _key, number, suffix, label: statLabel }) => (
              <StatCard key={_key}>
                <AnimatedNumber target={number || 0} suffix={suffix || ''} />
                <div className="stat-label">{statLabel}</div>
              </StatCard>
            ))}
          </StatsGrid>
        </StatsBar>
      )}
    </Section>
  );
}
